import { useMemo } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { CalendarClock, ChevronRight, Mail, MessageCircle, Phone } from 'lucide-react';
import { OPEN_STATUSES, metaOf, useLeads, type Lead } from '@/lib/leads';
import { phoneE164, primaryChannel, serviceLabel, telHref } from '@/lib/contact';
import { ago, when } from '@/lib/format';
import { Page, PageHeader } from '@/components/Shell';
import { Empty, ListSkeleton, LoadError, SectionLabel } from '@/components/ui';

/**
 * What needs the founder now, oldest first, and nothing else. When it is
 * empty, the day's admin is done. Briefs and invoices join this list in their
 * own phases (ADMIN_PLAN §4.2).
 */
export function TodayScreen() {
  const leads = useLeads('open');

  const { fresh, due, replied } = useMemo(() => {
    const all = leads.data ?? [];
    const now = Date.now();
    return {
      // An emailed reply marks its lead unread (inbound-email). New leads are
      // already listed above, so this is everyone else who wrote back.
      replied: all.filter((l) => l.status !== 'new' && !l.read_at),
      // Oldest first: the one who has waited longest is answered first.
      fresh: all.filter((l) => l.status === 'new').sort((a, b) => a.created_at.localeCompare(b.created_at)),
      due: all
        .filter(
          (l) =>
            l.status !== 'new' &&
            OPEN_STATUSES.includes(l.status as never) &&
            l.next_action_at &&
            new Date(l.next_action_at).getTime() <= now,
        )
        .sort((a, b) => (a.next_action_at ?? '').localeCompare(b.next_action_at ?? '')),
    };
  }, [leads.data]);

  return (
    <>
      <PageHeader title="Today" />
      <Page>
        {leads.isPending ? (
          <ListSkeleton rows={3} />
        ) : leads.error ? (
          <LoadError error={leads.error} retry={() => leads.refetch()} />
        ) : fresh.length === 0 && due.length === 0 && replied.length === 0 ? (
          <Empty title="Nothing is waiting on you">
            New enquiries and the follow-ups you set show up here, oldest first. Client briefs and unpaid invoices will
            join them as those parts of Desk arrive.
          </Empty>
        ) : (
          <div className="grid grid-cols-1 gap-8">
            {fresh.length > 0 && (
              <section aria-labelledby="t-new">
                <SectionLabel aside="oldest first">
                  <span id="t-new">New enquiries, not yet answered</span>
                </SectionLabel>
                <ul className="divide-y divide-edge overflow-hidden rounded-lg border border-edge bg-bg-raised">
                  {fresh.map((l) => (
                    <WaitingRow key={l.id} lead={l} reason={`Waiting ${ago(l.created_at)}`} />
                  ))}
                </ul>
              </section>
            )}
            {replied.length > 0 && (
              <section aria-labelledby="t-replied">
                <SectionLabel>
                  <span id="t-replied">Wrote back to you</span>
                </SectionLabel>
                <ul className="divide-y divide-edge overflow-hidden rounded-lg border border-edge bg-bg-raised">
                  {replied.map((l) => (
                    <WaitingRow key={l.id} lead={l} reason="New reply, unread" />
                  ))}
                </ul>
              </section>
            )}
            {due.length > 0 && (
              <section aria-labelledby="t-due">
                <SectionLabel>
                  <span id="t-due">Follow-ups due</span>
                </SectionLabel>
                <ul className="divide-y divide-edge overflow-hidden rounded-lg border border-edge bg-bg-raised">
                  {due.map((l) => (
                    <WaitingRow
                      key={l.id}
                      lead={l}
                      reason={[when(l.next_action_at!), l.next_action_note].filter(Boolean).join(' · ')}
                      dueIcon
                    />
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}
      </Page>
    </>
  );
}

/** One person waiting: who, why, and the one tap that reaches them. */
function WaitingRow({ lead, reason, dueIcon }: { lead: Lead; reason: string; dueIcon?: boolean }) {
  const service = serviceLabel(metaOf(lead).interest);
  const e164 = phoneE164(lead);
  const channel = primaryChannel(lead);
  const navigate = useNavigate();

  // Every shortcut opens the lead with its composer ready, so the saved reply
  // is filled in and the timeline records what happened. A call also dials.
  const quick =
    channel === 'whatsapp' || channel === 'email' ? (
      <Link
        to="/enquiries/$leadId"
        params={{ leadId: lead.id }}
        search={{ reach: channel }}
        className="btn btn-quiet btn-icon"
        aria-label={`${channel === 'whatsapp' ? 'WhatsApp' : 'Email'} ${lead.name}`}
      >
        {channel === 'whatsapp' ? <MessageCircle className="size-5" aria-hidden /> : <Mail className="size-5" aria-hidden />}
      </Link>
    ) : e164 ? (
      <a
        href={telHref(e164)}
        onClick={() => navigate({ to: '/enquiries/$leadId', params: { leadId: lead.id }, search: { reach: 'call' } })}
        className="btn btn-quiet btn-icon"
        aria-label={`Call ${lead.name}`}
      >
        <Phone className="size-5" aria-hidden />
      </a>
    ) : null;

  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <Link to="/enquiries/$leadId" params={{ leadId: lead.id }} className="group flex min-w-0 flex-1 items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold">
            {lead.name}
            {lead.company && <span className="font-normal text-fg-dim"> · {lead.company}</span>}
          </p>
          <p className="flex items-center gap-1.5 truncate text-sm text-fg-dim">
            {dueIcon && <CalendarClock className="size-3.5 shrink-0 text-warn" aria-hidden />}
            <span className="truncate">{[reason, service].filter(Boolean).join(' · ')}</span>
          </p>
        </div>
        <ChevronRight className="size-4 shrink-0 text-fg-faint transition-transform group-hover:translate-x-0.5" aria-hidden />
      </Link>
      {quick}
    </li>
  );
}
